import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { type MaybeRef, type MaybeRefOrGetter, toValue } from 'vue'
import {
  cancelInvoice,
  createRecurringCharge,
  getBillingCharge,
  getInvoice,
  getInvoiceConfig,
  getInvoiceMeters,
  getPaginatedInvoices,
  recreateInvoice,
  regenerateInvoice,
  removeRecurringCharge,
  saveCharges,
  saveInvoiceConfig,
  saveNotes,
  sendInvoice,
  updateInvoiceConfig,
  updateInvoiceMeterReadings,
  updateRecurringCharge
} from '@/api/billing/invoices'
import type { ComposableOptions, SortedField } from '@/types'
import { InvoiceStatus } from '@/api/billing/constants'
import { createLogger } from '@/helpers/logging'
import {
  CreateBillingChargeSchema,
  UpdateBillingChargeValuesSchema,
  type UpdateInvoiceMeterReading
} from '@/api/billing/schemas'
import { z } from 'zod'
import { useMutationWithInvalidation } from '@/shared/composables/useMutationWithInvalidation'
import type { SharedPaginationOptions } from '@/shared/types'
import { invoiceQueryKeys } from '@/features/invoices/query-keys'
import { deleteInvoiceCharge } from '@/features/invoices/api/deleteInvoiceCharge'
import { fetchInvoiceQueryOptions } from '@/features/invoices/queries'

type DataTypeForUpdate = Awaited<ReturnType<typeof getPaginatedInvoices>>
type PaginatedInvoiceWithUrls = DataTypeForUpdate['records'][number]

export function useCancelInvoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (invoiceId: MaybeRefOrGetter<number>) => cancelInvoice(toValue(invoiceId)),
    onSuccess(data, variables) {
      updateInvoiceInListedInvoiceQueryCache(
        queryClient,
        variables,
        invoice => ({ ...invoice, status: InvoiceStatus.Cancelled })
      )

      queryClient.invalidateQueries({ queryKey: ['invoices:activity', variables] }).catch(() => { })
    },
  })
}


export function useSendInvoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (invoiceId: MaybeRefOrGetter<number>) => sendInvoice(toValue(invoiceId)),
    async onSuccess(data, variables) {
      updateInvoiceInListedInvoiceQueryCache(
        queryClient,
        variables,
        invoice => ({ ...invoice, status: InvoiceStatus.Sent })
      )

      await Promise.allSettled([
        queryClient.invalidateQueries({ queryKey: ['invoices', variables] }),
        queryClient.invalidateQueries({ queryKey: ['invoices:activity', variables] }),
      ])
    },
  })
}


export const useRecreateInvoice = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (invoiceId: MaybeRefOrGetter<number>) => recreateInvoice(toValue(invoiceId)),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['invoices:list' ]}).catch(() => { })
    }
  })
}

export function useRegenerateInvoice() {
  return useMutation({
    mutationFn: (invoiceId: MaybeRefOrGetter<number>) =>
      regenerateInvoice(
        toValue(invoiceId)
      )
  })
}


export function usePaginatedInvoices(
  options: ComposableOptions<SharedPaginationOptions & {
    customerIds: number[]
    statuses: InvoiceStatus[]
    startAt?: Date
    endAt?: Date
  }>
) {
  return useQuery({
    queryKey: ['invoices:list', options],
    placeholderData: previous => previous,
    meta: {
      logger: createLogger(`composables/billing/usePaginatedInvoices`)
    },
    queryFn: () =>
      getPaginatedInvoices({
        page: toValue(options.page),
        pageSize: toValue(options.pageSize),
        sort: toValue(options.sorting),
        filter: toValue(options.filter),
        startAt: toValue(options.startAt),
        endAt: toValue(options.endAt),
        statuses: toValue(options.statuses),
        customerIds: toValue(options.customerIds)
      })
  })
}

export function updateInvoiceInListedInvoiceQueryCache(queryClient: QueryClient, invoiceId: MaybeRefOrGetter<number>, updater: (invoice: PaginatedInvoiceWithUrls) => PaginatedInvoiceWithUrls) {
  const invoiceIdValue = toValue(invoiceId)
  const queryData = queryClient
    .getQueriesData<DataTypeForUpdate>({ queryKey: ['invoices:list'] })
    .concat(queryClient.getQueriesData<DataTypeForUpdate>({ queryKey: invoiceQueryKeys.paginated() }))

  for (const [key, data] of queryData) {
    if (!data) {
      continue
    }

    const matchedIndex = data.records.findIndex(x => x.id === invoiceIdValue)

    if (matchedIndex < 0) {
      console.log(`updating cancelInvoice - no invoice with id ${invoiceIdValue} found for in data for query key ${JSON.stringify(key)}`)

      continue
    }

    queryClient.setQueryData<DataTypeForUpdate>(
      key,
      old => old
        ? {
          ...data,
          records: [...data.records].map(
            invoice => invoice.id === invoiceIdValue ? updater({ ...invoice }) : invoice
          )
        }
        : old
    )
  }
}

export function useGetInvoiceConfig(assetCode: MaybeRefOrGetter<string | undefined>) {

  return useQuery({
    queryKey: ['invoices:config', assetCode],
    meta: {
      logger: createLogger(`composables/billing/useGetInvoiceConfig`)
    },
    queryFn: () => getInvoiceConfig(toValue(assetCode)!),
     enabled() {
      const value = toValue(assetCode)
      return value !== undefined
    }
  })
}


type BillingConfig = {
  id: number,
  showMeterReadings: boolean,
  showSavings: boolean
}

export const useUpdateAssetInvoiceConfig = (assetCode: MaybeRefOrGetter<string | undefined>) => useMutationWithInvalidation(
  ({ id, showMeterReadings, showSavings } : BillingConfig) => saveInvoiceConfig(toValue(assetCode)!, {
    id: id,
    showMeterReadings: showMeterReadings,
    showSavings: showSavings
  }),
  [
    ['invoices:config', assetCode]
  ]
)

export function useCreateBillingCharge(assetCode: MaybeRefOrGetter<string>) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (charge: z.infer<typeof CreateBillingChargeSchema>) => createRecurringCharge(
      toValue(assetCode),
      charge
    ),
    async onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['invoices:config', assetCode]})
    }
  })
}

export function useGetBillingCharge(id: MaybeRefOrGetter<number|undefined>){
  return useQuery({
    queryKey: ['invoices:config:billingCharge',id],
    queryFn: () => getBillingCharge(toValue(id)!),
    enabled() {
      const value = toValue(id)
      return value !== undefined && value > 0
    }
  })
}

export function useUpdateBillingCharge(assetCode: MaybeRefOrGetter<string>) {
  return useMutationWithInvalidation(
    async (charge: z.infer<typeof UpdateBillingChargeValuesSchema>) => updateRecurringCharge(
      toValue(assetCode),
      charge
    ),
    [
      ['invoices:config', assetCode],
      (input) => ['invoices:config:billingCharge', input.id],
    ]
  )
}

export function useRemoveBillingCharge(assetCode: MaybeRefOrGetter<string>) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (chargeId: MaybeRefOrGetter<number>) => removeRecurringCharge(
      toValue(assetCode),
      toValue(chargeId)
    ),
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: ['invoices:config', assetCode]})
    }
  })
}

export function useDeleteInvoiceCharge(invoiceId: MaybeRefOrGetter<number>) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (chargeId: MaybeRefOrGetter<number>) => await deleteInvoiceCharge(toValue(invoiceId), toValue(chargeId)),
    async onSuccess(){
      await queryClient.invalidateQueries({queryKey: invoiceQueryKeys.invoice(invoiceId)})
    }
  })
}

export function useGetInvoice (invoiceId: MaybeRefOrGetter<number>){

  return useQuery({
    queryKey: ['invoices',invoiceId],
    placeholderData: previous => previous,
    meta: {
      logger: createLogger(`composables/billing/useGetInvoice`)
    },
    queryFn: () => getInvoice(toValue(invoiceId))
  })
}

export function useSaveCharges(invoiceId : number){
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (invoiceCharges: { id: number, quantity: number, description: string }[]) => saveCharges(invoiceId, invoiceCharges),
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId]})
    }
  })
}

type InvoiceConfig = {
  invoiceId: number,
  showMeterReadings: MaybeRef<boolean>,
  showSavings: MaybeRef<boolean>,
  poNumber: MaybeRef<string>
}

export function useUpdateInvoiceConfig(){
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (invoiceConfig: InvoiceConfig) => updateInvoiceConfig({
      invoiceId: invoiceConfig.invoiceId,
      showMeterReadings: toValue(invoiceConfig.showMeterReadings),
      showSavings: toValue(invoiceConfig.showSavings),
      poNumber: toValue(invoiceConfig.poNumber)
    }),
    async onSuccess(data, variables) {
      await Promise.allSettled([
        queryClient.invalidateQueries({ queryKey: ['invoices', variables.invoiceId] }),
      ])
    }
  })
}

export function useSaveNotes(invoiceId : number){
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (notes: string) => saveNotes(invoiceId, notes),
    async onSuccess() {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId ]}),
        fetchInvoiceQueryOptions(invoiceId).queryKey
      ])
    }
  })
}

export function useUpdateInvoiceMeterReadings(invoiceId: number, updatingConfig: boolean=true) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invoiceMeterReadings: UpdateInvoiceMeterReading[]) => {

      await updateInvoiceMeterReadings(invoiceId, invoiceMeterReadings, updatingConfig)
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['invoices', invoiceId] }).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['invoices:list' ]}).catch(() => { })
      queryClient.invalidateQueries({ queryKey: ['invoices:meterReading',invoiceId ]}).catch(() => { });
    }
  });
}

export function useGetInvoiceMeterReadings(invoiceId: number) {
  return useQuery({
    queryKey: ['invoices:meterReading',invoiceId],

    queryFn: () => getInvoiceMeters(invoiceId)
  })
}
